/**
 * Web push subscription module — Batch 4 slice 4b step 3 (12 Jun 2026).
 *
 * Plain async functions (not hooks) for opting in/out of web push.
 * SettingsPage wraps these in React state.
 *
 *   subscribeToPush:
 *     1. Feature-detect (serviceWorker + PushManager + Notification API)
 *     2. Guard against iOS-non-installed — push works on iOS only when
 *        the site is installed to the home screen (iOS 16.4+); browser
 *        APIs are present in iOS Safari before install but subscribe
 *        silently fails to deliver
 *     3. Request notification permission (browser prompt — only if
 *        not already granted)
 *     4. Resolve the service worker registration
 *     5. Check for existing PushSubscription
 *     6. If none: fetch VAPID public key from /v1/push/vapid-public-key
 *        (Cloudflare-edge-cached; usually free), convert base64url to
 *        Uint8Array, call pushManager.subscribe()
 *     7. POST the subscription.toJSON() to /v1/push/subscribe — the
 *        server's upsert handles both fresh and refreshed cases
 *
 *   unsubscribeFromPush:
 *     1. Get current subscription — idempotent if none
 *     2. DELETE /v1/push/subscribe FIRST — server state drives future
 *        sends; if browser succeeds but server fails, we'd have a
 *        server row with no browser counterpart, and slice 4c's
 *        410-handling on sendPushToUser would self-heal it. The
 *        reverse failure mode (browser still subscribed but server
 *        forgot) leaves no recovery path from the client.
 *     3. subscription.unsubscribe() at the browser level
 *
 * Errors throw PushError with a machine-readable code so SettingsPage
 * can render the right translated copy. API errors propagate as
 * ApiError from apiFetch unchanged.
 */

import { apiFetch } from './api';

// ============================================================================
// Types
// ============================================================================

export type PushSupportKind = 'supported' | 'unsupported' | 'needs-install';

export interface PushStatus {
  /**
   * Capability of this browser + install combo:
   *   - 'supported'      — full support
   *   - 'unsupported'    — missing APIs, or no SW registered (e.g. dev mode)
   *   - 'needs-install'  — iOS that requires home-screen install for push
   */
  support: PushSupportKind;
  /**
   * Current Notification.permission state — mirrors the DOM API.
   * 'default' means the user hasn't been asked yet.
   */
  permission: NotificationPermission;
  /** Whether this device currently has an active push subscription. */
  subscribed: boolean;
}

export type PushErrorCode =
  | 'unsupported'
  | 'needs_install'
  | 'permission_denied'
  | 'browser_failure';

export class PushError extends Error {
  public readonly code: PushErrorCode;
  constructor(code: PushErrorCode, message: string) {
    super(message);
    this.name = 'PushError';
    this.code = code;
  }
}

// ============================================================================
// Capability detection
// ============================================================================

/**
 * Synchronous capability check — no API calls. Returns one of three
 * states so the SettingsPage UI can render the right affordance.
 */
function getPushSupport(): PushSupportKind {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator)) return 'unsupported';
  if (!('PushManager' in window)) return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  if (isIOSNotInstalled()) return 'needs-install';
  return 'supported';
}

/**
 * iOS-specific gate: push works only when the site is installed to
 * the home screen as a PWA (iOS 16.4+). The PushManager API IS
 * present in iOS Safari even pre-install — subscribe calls succeed
 * but no notifications arrive. Detect this dead-end and gate the
 * toggle so guests see "install first" instead of a silent fail.
 */
function isIOSNotInstalled(): boolean {
  const ua = navigator.userAgent;
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes('Mac') && navigator.maxTouchPoints > 1);
  if (!isIOS) return false;
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return !standalone;
}

// ============================================================================
// Status read
// ============================================================================

/**
 * Resolve the current push state without subscribing. Called on
 * SettingsPage mount to render the toggle in the right initial
 * position.
 *
 * SW not yet active? We report 'unsupported' rather than hanging
 * on navigator.serviceWorker.ready (which never resolves in dev
 * mode where registerServiceWorker is a no-op). getRegistration()
 * returns immediately with undefined in that case.
 */
export async function getPushStatus(): Promise<PushStatus> {
  const support = getPushSupport();
  if (support !== 'supported') {
    return {
      support,
      permission: 'default',
      subscribed: false,
    };
  }
  const permission = Notification.permission;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration || !registration.active) {
    return {
      support: 'unsupported',
      permission,
      subscribed: false,
    };
  }
  const existing = await registration.pushManager.getSubscription();
  return {
    support: 'supported',
    permission,
    subscribed: existing !== null,
  };
}

// ============================================================================
// Subscribe / unsubscribe
// ============================================================================

/**
 * Subscribe this device to push notifications. Caller MUST supply
 * getToken from Clerk's useAuth().getToken — the POST /v1/push/
 * subscribe call carries the user's JWT.
 *
 * If a browser-level subscription already exists, we reuse it and
 * POST again — the server's upsertPushSubscription is idempotent
 * and this protects against permission-state divergence where the
 * browser thinks subscribed but the server lost the row.
 *
 * Throws PushError on browser-API failure; propagates ApiError
 * from apiFetch unchanged on server failure. SettingsPage translates.
 */
export async function subscribeToPush(
  getToken: () => Promise<string | null>,
): Promise<void> {
  const support = getPushSupport();
  if (support !== 'supported') {
    throw new PushError(
      support === 'needs-install' ? 'needs_install' : 'unsupported',
      'Push not supported in this environment',
    );
  }

  // Permission request — browser prompts only when 'default'; for
  // 'granted' or 'denied' returns the existing decision without
  // re-prompting.
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError(
      'permission_denied',
      'Notification permission denied',
    );
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration || !registration.active) {
    throw new PushError(
      'browser_failure',
      'Service worker not active — reload the page and try again',
    );
  }

  let subscription = await registration.pushManager.getSubscription();
  if (subscription === null) {
    const { publicKey } = await apiFetch<{ publicKey: string }>(
      '/v1/push/vapid-public-key',
    );
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(publicKey),
    });
  }

  // PushSubscription.toJSON() produces { endpoint, keys: { p256dh, auth } }
  // matching the server's SubscribeSchema exactly.
  await apiFetch('/v1/push/subscribe', {
    method: 'POST',
    body: subscription.toJSON(),
    getToken,
  });
}

/**
 * Unsubscribe this device. Idempotent — if no subscription exists,
 * returns without server contact.
 */
export async function unsubscribeFromPush(
  getToken: () => Promise<string | null>,
): Promise<void> {
  const support = getPushSupport();
  if (support !== 'supported') return;

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration || !registration.active) return;

  const subscription = await registration.pushManager.getSubscription();
  if (subscription === null) return;

  // Server-first: see file header for ordering rationale.
  await apiFetch('/v1/push/subscribe', {
    method: 'DELETE',
    body: { endpoint: subscription.endpoint },
    getToken,
  });

  await subscription.unsubscribe();
}

// ============================================================================
// base64url → Uint8Array
// ============================================================================

/**
 * Convert a base64url-encoded string (as served by /v1/push/vapid-
 * public-key) to the Uint8Array shape that PushManager.subscribe()
 * requires for applicationServerKey.
 *
 * Base64url uses `-` for `+`, `_` for `/`, and omits `=` padding.
 * atob() needs standard base64 with padding, so we normalise.
 */
function base64UrlToUint8Array(base64Url: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  // Explicit ArrayBuffer allocation: TS 5.7+ narrowed Uint8Array to be
  // generic over its buffer type, and `new Uint8Array(length)` resolves
  // to `Uint8Array<ArrayBufferLike>` (the SharedArrayBuffer-inclusive
  // union). PushManager.subscribe()'s applicationServerKey requires
  // `BufferSource`, which is `ArrayBufferView<ArrayBuffer> | ArrayBuffer`
  // — SharedArrayBuffer not allowed. Allocating an ArrayBuffer up front
  // and constructing the view over it pins the generic to `ArrayBuffer`.
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}
